/**
 * ProposalsPanel (TODAY-QUOTES, Seth 2026-08-28: "clear areas of part tickets,
 * QUOTES, agreements, on-call") — the open proposal pipeline on the morning
 * page: what's out with customers (sent / viewed, aging) and what's been
 * accepted but has no install job yet. Each row opens the actual proposal —
 * the click Seth expected the board card to have.
 */
import { useQuery } from "@tanstack/react-query";
import { FileText, ExternalLink, MessageSquare } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { CustomerLink } from "@/components/links";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { displayCustomerName } from "@/lib/format";

interface OpenQuote {
  id: string;
  custno: string | null;
  locno: string | null;
  quote_type: string;
  status: string;
  token: string | null;
  job_id: string | null;
  created_at: string;
  viewed_at: string | null;
  /** PROPOSAL-FLOW-02: staff opened the customer view (presented in person) — a draft with this is live with a customer. */
  presented_at: string | null;
  name: string;
  /** QUOTE-ASK-01: homeowner questions nobody has answered yet. */
  open_questions: number;
}

export function useOpenProposals() {
  return useQuery({
    queryKey: ["today_open_proposals"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<OpenQuote[]> => {
      const { data, error } = await supabase
        .from("pegasus_quote")
        .select("id, custno, locno, quote_type, status, token, job_id, created_at, viewed_at, presented_at")
        // PROPOSAL-FLOW-02: sent / viewed / accepted, plus drafts somebody has
        // presented in person and never sent — those are out with a customer
        // too. A draft nobody has shown stays off the morning page.
        .or("status.in.(sent,viewed,accepted),and(status.eq.draft,presented_at.not.is.null)")
        // QUOTE-FAV-01 / PRACTICE-01: presets and sandbox builds never surface.
        .eq("is_template", false)
        .eq("is_practice", false)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      const rows = ((data ?? []) as Omit<OpenQuote, "name" | "open_questions">[])
        // accepted + job already made = the Installs section's business, not ours
        .filter((q) => !(q.status === "accepted" && q.job_id));
      // QUOTE-ASK-01: unanswered homeowner questions ride the row (and sort it first).
      const asks = new Map<string, number>();
      if (rows.length) {
        const { data: ms } = await supabase
          .from("pegasus_quote_message")
          .select("quote_id")
          .eq("direction", "in")
          .is("replied_at", null)
          .in("quote_id", rows.map((r) => r.id));
        for (const m of (ms ?? []) as { quote_id: string }[]) asks.set(m.quote_id, (asks.get(m.quote_id) ?? 0) + 1);
      }
      const custnos = [...new Set(rows.map((r) => r.custno).filter(Boolean))] as string[];
      const names = new Map<string, string>();
      if (custnos.length) {
        const [{ data: esc }, { data: peg }] = await Promise.all([
          supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos),
          supabase.from("pegasus_customer").select("custno, fullname").in("custno", custnos),
        ]);
        for (const c of (esc ?? []) as { custno: string; fullname: string | null }[]) if (c.fullname) names.set(c.custno, c.fullname);
        for (const c of (peg ?? []) as { custno: string; fullname: string | null }[]) if (c.fullname && !names.has(c.custno)) names.set(c.custno, c.fullname);
      }
      return rows
        .map((r) => ({ ...r, open_questions: asks.get(r.id) ?? 0, name: displayCustomerName(null, r.custno ? names.get(r.custno) ?? null : null) || "Customer" }))
        .sort((a, b) => (b.open_questions > 0 ? 1 : 0) - (a.open_questions > 0 ? 1 : 0));
    },
  });
}

const ageDays = (iso: string): number => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

export function ProposalsPanel() {
  const { data, isLoading } = useOpenProposals();
  const rows = data ?? [];
  if (isLoading || rows.length === 0) return null; // quiet section hides itself
  return (
    <Card className="divide-y divide-slate-100 overflow-hidden">
      {rows.map((q) => {
        const accepted = q.status === "accepted";
        const viewed = q.status === "viewed";
        const presentedOnly = q.status === "draft"; // only presented drafts make the list
        const days = ageDays(q.created_at);
        return (
          <div key={q.id} className="group flex items-center gap-3 px-4 py-2.5">
            <FileText className={cn("h-4 w-4 shrink-0", accepted ? "text-emerald-500" : viewed ? "text-sky-500" : presentedOnly ? "text-indigo-500" : "text-amber-500")} />
            {q.open_questions > 0 ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-200/80 px-2 py-0.5 text-[11px] font-bold text-amber-900">
                <MessageSquare className="h-3 w-3" /> {q.open_questions === 1 ? "Question waiting — reply" : `${q.open_questions} questions waiting`}
              </span>
            ) : (
              <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                accepted ? "bg-emerald-50 text-emerald-700" : viewed ? "bg-sky-50 text-sky-700" : presentedOnly ? "bg-indigo-50 text-indigo-700" : "bg-amber-50 text-amber-800")}>
                {accepted ? "Accepted — start the job" : viewed ? "Viewed — thinking it over" : presentedOnly ? "Presented in person — link not sent" : "Sent — awaiting decision"}
              </span>
            )}
            <div className="min-w-0 flex-1 text-sm text-slate-700">
              {q.custno ? (
                <CustomerLink custno={q.custno} locno={q.locno ?? undefined} className="text-sm font-semibold">{q.name}</CustomerLink>
              ) : (
                <span className="font-semibold">{q.name}</span>
              )}
              {" — "}
              {q.quote_type === "install" ? "system replacement proposal" : "repair options"}
            </div>
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
              {days === 0 ? "today" : `${days}d out`}
            </span>
            <a
              href={`/quotes/${q.id}`}
              title={q.open_questions > 0 ? "Open the quote — the question and the reply buttons are on its Activity" : "Open the quote"}
              className={cn("inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold transition-colors",
                q.open_questions > 0 ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}
            >
              {q.open_questions > 0 ? "Reply" : "Open"} <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        );
      })}
    </Card>
  );
}
